import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeftRight, Ban, Check, ChevronLeft, ChevronRight, Eye, Flag, History, Image as ImageIcon, MessageSquare, ShieldAlert, UserX } from "lucide-react";
import { api, errMsg, fmtDate, media, GENDER_LABEL, METHOD_LABEL, SERVICE_LABEL, timeAgo } from "@/lib/api";
import { useAdmin } from "./useAdmin";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function riskColor(band) {
  if (band === "held" || band === "high") return "bg-[#B42318]";
  if (band === "medium") return "bg-[#9A6B12]";
  return "bg-[#1F7A4D]";
}

function ActionDialog({ open, onOpenChange, title, description, placeholder, confirmLabel, destructive, onConfirm, requireReason = true, checkFlags }) {
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setError("");
    if (requireReason === true && reason.trim().length < 5) {
      setError("Give a reason (5+ characters)");
      return;
    }
    if (checkFlags && !ack) {
      setError("Confirm you've reviewed the flagged terms");
      return;
    }
    setBusy(true);
    try {
      await onConfirm({ reason: reason.trim(), ack });
      setReason("");
      setAck(false);
      onOpenChange(false);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="adm-card border-admin-border bg-admin-surface sm:max-w-lg" data-testid="admin-action-dialog">
        <DialogHeader>
          <DialogTitle className="text-admin-text">{title}</DialogTitle>
          <DialogDescription className="text-admin-muted">{description}</DialogDescription>
        </DialogHeader>
        {checkFlags && (
          <label className="flex items-start gap-3 rounded-[8px] border border-[#E9D3A6] bg-[#FFF8EB] p-3 text-sm text-[#7A520B]">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" data-testid="admin-action-ack-flags" />
            I've reviewed the flagged terms and understand their context.
          </label>
        )}
        {requireReason && (
          <div>
            <label htmlFor="action-reason" className="adm-label">Reason {requireReason === "optional" ? "(optional)" : "(required)"}</label>
            <textarea id="action-reason" rows={4} className="adm-input mt-2" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder={placeholder} data-testid="admin-action-reason-textarea" />
          </div>
        )}
        {error && <p className="text-sm text-[#B42318]" role="alert">{error}</p>}
        <DialogFooter className="gap-2">
          <button onClick={() => onOpenChange(false)} className="adm-btn adm-btn-outline" data-testid="admin-action-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className={`adm-btn ${destructive ? "adm-btn-danger" : "adm-btn-primary"}`} data-testid="admin-action-confirm">
            {busy ? "Working..." : confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContentPane({ title, content }) {
  if (!content) return <p className="text-sm text-admin-muted">—</p>;
  return (
    <div className="space-y-3 text-sm text-admin-text" data-testid="admin-case-content">
      {title && <p className="text-xs font-semibold uppercase tracking-wider text-admin-muted">{title}</p>}
      <div>
        <p className="text-xs text-admin-muted">Display name · age</p>
        <p className="font-medium">{content.display_name} · {content.age}</p>
      </div>
      <div>
        <p className="text-xs text-admin-muted">Headline</p>
        <p>{content.headline}</p>
      </div>
      <div>
        <p className="text-xs text-admin-muted">Description</p>
        <p className="whitespace-pre-line">{content.description}</p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <p className="text-xs text-admin-muted">Category</p>
          <p>{GENDER_LABEL[content.gender] || content.gender} · {content.service_types?.map((s) => SERVICE_LABEL[s] || s).join(" & ")}{content.ethnicity_label ? ` · ${content.ethnicity_label}` : ""}</p>
        </div>
        <div>
          <p className="text-xs text-admin-muted">Area</p>
          <p>{content.primary_area_name || content.primary_area}{content.nearby_area_names?.length ? " + " + content.nearby_area_names.join(", ") : ""}</p>
        </div>
      </div>
      <div>
        <p className="text-xs text-admin-muted">Price range</p>
        <p>{content.price_min != null && content.price_max != null ? `£${content.price_min} – £${content.price_max}` : "— not published"}</p>
      </div>
      <div>
        <p className="text-xs text-admin-muted">Contact</p>
        <p>{METHOD_LABEL[content.contact_method] || content.contact_method}: <code className="font-mono text-admin-muted">{content.contact_value || "—"}</code></p>
      </div>
    </div>
  );
}

export default function AdminCaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { can } = useAdmin();
  useSeo({ title: `Case ${id} | LUX`, ...PRIVATE_SEO });
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "case", id],
    queryFn: async () => (await api.get(`/admin/cases/${id}`)).data,
  });

  const [action, setAction] = useState(null); // approve|request_changes|suspend|escalate|ban|close|reject_photo
  const [photoIndex, setPhotoIndex] = useState(0);
  const [photoForReject, setPhotoForReject] = useState(null);

  const perms = data?.permissions || {};
  const c = data?.case;
  const isOpen = useMemo(() => c && ["open", "escalated"].includes(c.status), [c]);

  if (isLoading) return <p className="text-sm text-admin-muted">Loading case...</p>;
  if (error || !data) return <p className="text-sm text-[#B42318]">Case not available.</p>;

  const act = async (payload) => {
    try {
      await api.post(`/admin/cases/${id}/actions`, payload);
      toast.success(`Case ${payload.action.replace(/_/g, " ")}`);
      client.invalidateQueries({ queryKey: ["admin"] });
      await refetch();
    } catch (err) {
      throw err;
    }
  };

  const requestExport = async () => {
    const reason = window.prompt("Reason for export (second Compliance approver will review)");
    if (!reason || reason.length < 5) return;
    try {
      await api.post(`/admin/cases/${id}/exports`, { reason });
      toast.success("Export request submitted for second-approver review");
      await refetch();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const photo = data.photos?.[photoIndex];
  const total = data.photos?.length || 0;

  return (
    <div className="space-y-5" data-testid="admin-case-detail">
      <header className="flex flex-wrap items-start gap-3">
        <Link to="/admin/cases" className="adm-btn adm-btn-outline" data-testid="admin-case-back">
          <ChevronLeft className="h-4 w-4" /> Queue
        </Link>
        <div className="min-w-0 flex-1">
          <p className="adm-label">Case #{c.number}</p>
          <h1 className="mt-1 text-2xl font-semibold text-admin-text" data-testid="admin-case-subject-name">
            {c.snapshot?.display_name || data.profile?.slug || "Report"}
          </h1>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-admin-muted">
            <span>{c.subject_type} · {c.queue}</span>
            <span>Created {fmtDate(c.created_at, true)}</span>
            <span>Status: {c.status.replace(/_/g, " ")}</span>
            {data.profile?.is_demo && <span className="adm-pill border-admin-border bg-admin-surface2 text-admin-muted">Demo</span>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-xs text-admin-muted">Risk score</p>
            <p className="text-xl font-semibold text-admin-text">{c.score} <span className="text-xs uppercase text-admin-muted">{c.band}</span></p>
          </div>
          <div className="h-10 w-20 overflow-hidden rounded-full bg-admin-surface2">
            <div className={`h-full ${riskColor(c.band)}`} style={{ width: `${Math.min(100, c.score)}%` }} />
          </div>
        </div>
      </header>

      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        <div className="space-y-5">
          {/* Photos */}
          {data.photos?.length > 0 && (
            <section className="adm-card overflow-hidden" data-testid="admin-case-photos">
              <div className="relative bg-black">
                <div className="aspect-[4/3] sm:aspect-video">
                  {photo && <img src={media(photo.url)} alt="" className="h-full w-full object-contain" />}
                </div>
                {total > 1 && (
                  <>
                    <button onClick={() => setPhotoIndex((photoIndex + total - 1) % total)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-admin-text hover:bg-white" aria-label="Prev photo" data-testid="admin-case-photo-prev">
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button onClick={() => setPhotoIndex((photoIndex + 1) % total)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-admin-text hover:bg-white" aria-label="Next photo" data-testid="admin-case-photo-next">
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </>
                )}
                <span className="absolute right-3 top-3 rounded-full bg-black/70 px-2 py-0.5 text-xs text-white">{photoIndex + 1} / {total}</span>
              </div>
              <div className="flex items-start justify-between gap-3 p-4">
                <div>
                  <p className="text-xs text-admin-muted">Photo {photo?.id?.slice(0, 8)}... · {photo?.kind} · {photo?.status}</p>
                  {photo?.duplicates?.length > 0 && (
                    <p className="mt-1 text-xs text-[#9A6B12]">{photo.duplicates.length} duplicate(s) detected on other accounts</p>
                  )}
                  {photo?.ai?.flags && Object.values(photo.ai.flags).some(Boolean) && (
                    <p className="mt-1 text-xs text-[#B42318]">AI flags: {Object.entries(photo.ai.flags).filter(([, v]) => v).map(([k]) => k).join(", ")}</p>
                  )}
                </div>
                {perms.decide && photo && photo.status !== "rejected" && (
                  <button onClick={() => setPhotoForReject(photo)} className="adm-btn adm-btn-warn" data-testid="admin-case-reject-photo-button">
                    Reject this photo
                  </button>
                )}
              </div>
              {total > 1 && (
                <div className="flex gap-2 overflow-x-auto border-t border-admin-border bg-admin-surface2 p-2">
                  {data.photos.map((p, i) => (
                    <button key={p.id} onClick={() => setPhotoIndex(i)}
                      className={`h-14 w-14 shrink-0 overflow-hidden rounded-[6px] border-2 ${i === photoIndex ? "border-[#111827]" : "border-admin-border opacity-60"}`}
                      aria-label={`Photo ${i + 1}`}>
                      <img src={media(p.thumb)} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Content diff */}
          <section className="adm-card p-5" data-testid="admin-case-content-section">
            <div className="grid gap-6 lg:grid-cols-2">
              <ContentPane title="Submitted (pending)" content={data.submitted} />
              <ContentPane title="Approved" content={data.approved} />
            </div>
            {data.changed_fields?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                <ArrowLeftRight className="h-4 w-4 text-admin-muted" aria-hidden="true" />
                <p className="text-xs font-semibold text-admin-muted">Changed:</p>
                {data.changed_fields.map((f) => (
                  <span key={f.key} className="adm-pill border-[#111827] bg-[#111827] text-white">{f.label}</span>
                ))}
              </div>
            )}
          </section>

          {/* Story */}
          {data.story && (
            <section className="adm-card p-5" data-testid="admin-case-story">
              <p className="adm-label">Story</p>
              <div className="mt-3 flex gap-4">
                {data.story.photo && <img src={media(data.story.photo.thumb)} alt="" className="h-32 w-24 shrink-0 rounded-[8px] object-cover" />}
                <div>
                  <p className="text-sm text-admin-text">Status: {data.story.status}</p>
                  <p className="mt-1 text-xs text-admin-muted">Posted {fmtDate(data.story.created_at, true)}</p>
                  {data.story.reject_reason && <p className="mt-1 text-xs text-[#B42318]">{data.story.reject_reason}</p>}
                </div>
              </div>
            </section>
          )}

          {/* Reports */}
          {data.reports?.length > 0 && (
            <section className="adm-card p-5" data-testid="admin-case-reports">
              <p className="adm-label">Reports ({data.reports.length})</p>
              <ul className="mt-3 space-y-3">
                {data.reports.map((r) => (
                  <li key={r.id} className="rounded-[8px] border border-admin-border bg-admin-surface2 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-admin-text">{r.reason_label || r.reason}</p>
                      <span className="text-xs text-admin-muted">{timeAgo(r.created_at)} · #{r.number}</span>
                    </div>
                    {r.details && <p className="mt-1 text-sm text-admin-text">{r.details}</p>}
                    {r.reporter_contact && <p className="mt-1 text-xs text-admin-muted">Reporter contact: <span className="text-admin-text">{r.reporter_contact}</span></p>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* History */}
          <section className="adm-card p-5" data-testid="admin-case-history">
            <p className="adm-label"><History className="mr-1 inline h-3.5 w-3.5" /> Audit history</p>
            {data.history?.length === 0 ? (
              <p className="mt-3 text-sm text-admin-muted">No actions yet.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {data.history.map((h) => (
                  <li key={h.seq} className="flex items-center justify-between gap-3 border-b border-admin-border pb-2 text-xs last:border-b-0 last:pb-0">
                    <span className="font-mono text-admin-muted">#{h.seq}</span>
                    <span className="flex-1 text-admin-text">{h.actor_name || "System"} · {h.action}</span>
                    <span className="text-admin-muted">{fmtDate(h.at, true)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* Right panel: signals + actions */}
        <aside className="space-y-5">
          <section className="adm-card p-5" data-testid="admin-case-signals">
            <p className="adm-label">Signals</p>
            {c.signals?.length === 0 ? (
              <p className="mt-3 text-sm text-admin-muted">No signals.</p>
            ) : (
              <ul className="mt-3 space-y-2 text-sm">
                {c.signals.map((s, i) => (
                  <li key={i} className="flex items-center justify-between gap-3">
                    <span className="text-admin-text">{s.label}</span>
                    <span className="font-mono text-[#B42318]">+{s.points}</span>
                  </li>
                ))}
              </ul>
            )}
            {c.blocked && (
              <p className="mt-4 flex items-start gap-2 rounded-[6px] border border-[#F2B4B4] bg-[#FEECEC] p-2 text-xs text-[#B42318]" data-testid="admin-case-blocked">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Flagged terms detected. You must confirm review before approving.
              </p>
            )}
          </section>

          {data.account && (
            <section className="adm-card p-5" data-testid="admin-case-account">
              <p className="adm-label">Account</p>
              <p className="mt-2 text-sm text-admin-text">{data.account.email}</p>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-admin-muted">
                <span>Status: <strong className="text-admin-text">{data.account.status}</strong></span>
                <span>Devices: <strong className="text-admin-text">{data.account.devices}</strong></span>
                <span>Other cases: <strong className="text-admin-text">{data.account.other_cases}</strong></span>
              </div>
              {data.profile?.slug && (
                <Link to={`/profile/${data.profile.slug}`} target="_blank" rel="noreferrer" className="adm-btn adm-btn-outline mt-3 w-full">
                  <Eye className="h-4 w-4" /> Open public profile
                </Link>
              )}
            </section>
          )}

          <section className="adm-card space-y-2 p-5" data-testid="admin-case-actions">
            <p className="adm-label">Actions</p>
            {isOpen && perms.decide ? (
              <>
                <button onClick={() => setAction("approve")} className="adm-btn adm-btn-primary w-full" data-testid="admin-case-approve-button">
                  <Check className="h-4 w-4" /> Approve
                </button>
                <button onClick={() => setAction("request_changes")} className="adm-btn adm-btn-warn w-full" data-testid="admin-case-request-changes-button">
                  <MessageSquare className="h-4 w-4" /> Request changes
                </button>
                <button onClick={() => setAction("suspend")} className="adm-btn adm-btn-danger w-full" data-testid="admin-case-suspend-button">
                  <UserX className="h-4 w-4" /> Suspend
                </button>
              </>
            ) : (
              <p className="text-sm text-admin-muted">{isOpen ? "You don't have permission to decide this case." : "This case is already decided."}</p>
            )}
            {isOpen && perms.escalate && (
              <button onClick={() => setAction("escalate")} className="adm-btn adm-btn-outline w-full" data-testid="admin-case-escalate-button">
                <ShieldAlert className="h-4 w-4" /> Escalate to Compliance
              </button>
            )}
            {perms.compliance && (
              <button onClick={() => setAction("ban")} className="adm-btn adm-btn-danger w-full" data-testid="admin-case-ban-button">
                <Ban className="h-4 w-4" /> Ban account
              </button>
            )}
            {perms.compliance && (
              <button onClick={() => setAction("close")} className="adm-btn adm-btn-outline w-full" data-testid="admin-case-close-button">
                Close case
              </button>
            )}
            {perms.export && (
              <button onClick={requestExport} className="adm-btn adm-btn-outline w-full" data-testid="admin-case-export-button">
                Request evidence export
              </button>
            )}
          </section>

          {data.suspension && (
            <section className="adm-card border-[#F2B4B4] bg-[#FEECEC] p-5 text-[#B42318]" data-testid="admin-case-suspension-info">
              <p className="adm-label text-[#B42318]">Suspended</p>
              <p className="mt-2 text-sm">{data.suspension.reason}</p>
              <p className="mt-1 text-xs">By {data.suspension.by_role} · {fmtDate(data.suspension.at, true)}</p>
            </section>
          )}
        </aside>
      </div>

      <ActionDialog
        open={action === "approve"} onOpenChange={() => setAction(null)}
        title="Approve this case"
        description="The pending content becomes the approved version."
        placeholder="Note for the audit log (optional)"
        confirmLabel="Approve"
        checkFlags={!!c.blocked}
        requireReason="optional"
        onConfirm={({ reason, ack }) => act({ action: "approve", reason, acknowledge_flags: ack })}
      />
      <ActionDialog
        open={action === "request_changes"} onOpenChange={() => setAction(null)}
        title="Request changes"
        description="The advertiser will see this reason in their dashboard."
        placeholder="e.g. Please remove third-party watermarks…"
        confirmLabel="Request changes"
        onConfirm={({ reason }) => act({ action: "request_changes", reason })}
      />
      <ActionDialog
        open={action === "suspend"} onOpenChange={() => setAction(null)}
        title="Suspend account"
        description="The advertiser can submit one appeal. Reviewed by a different staff member."
        placeholder="e.g. Repeated banned terms after warning…"
        confirmLabel="Suspend"
        destructive
        onConfirm={({ reason }) => act({ action: "suspend", reason })}
      />
      <ActionDialog
        open={action === "escalate"} onOpenChange={() => setAction(null)}
        title="Escalate to Compliance"
        description="Moves this case to the Compliance queue."
        placeholder="Why does this need Compliance…"
        confirmLabel="Escalate"
        onConfirm={({ reason }) => act({ action: "escalate", reason })}
      />
      <ActionDialog
        open={action === "ban"} onOpenChange={() => setAction(null)}
        title="Ban account"
        description="Permanently bans the account and all linked hashes. This cannot be undone easily."
        placeholder="Reason for the ban…"
        confirmLabel="Ban account"
        destructive
        onConfirm={({ reason }) => act({ action: "ban", reason })}
      />
      <ActionDialog
        open={action === "close"} onOpenChange={() => setAction(null)}
        title="Close case"
        description="Close without changing content state."
        placeholder="Reason / note"
        confirmLabel="Close"
        requireReason="optional"
        onConfirm={({ reason }) => act({ action: "close", reason })}
      />
      <ActionDialog
        open={!!photoForReject} onOpenChange={() => setPhotoForReject(null)}
        title="Reject this photo"
        description="The advertiser will see this reason and can replace the photo."
        placeholder="e.g. Watermark visible…"
        confirmLabel="Reject photo"
        destructive
        onConfirm={({ reason }) => act({ action: "reject_photo", reason, photo_id: photoForReject?.id })}
      />
    </div>
  );
}
